import { useMemo, useState } from "react";
import { deleteDocument, dismissJob, documentDownloadUrl, documentText } from "./api";

/**
 * Källetiketten per rad (AA-7: "Skolan ska kunna se allt Aerro vet om dem,
 * oavsett hur det kom in"). Utan märkningen ser en skrapad sida ut som något
 * skolan lagt in själv, och då blir det obegripligt att den inte går att radera.
 */
const KALLA = {
  scrape: "Webbplats",
  email: "Utskick",
  upload: "Uppladdat",
};


/** Dagar kvar innan ett dokument slutar svara. null när det inte går ut. */
function daysLeft(validUntil) {
  if (!validUntil) return null;
  const dag = 24 * 60 * 60 * 1000;
  return Math.round((new Date(`${validUntil}T00:00:00`) - new Date().setHours(0, 0, 0, 0)) / dag);
}

export default function Documents({ user, documents, jobs, onChange }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  // Fritext och källa. 100 dokument varav 69 skrapade sidor gör att skolans
  // egna uppladdningar drunknar — sökningen finns för att hitta dem igen.
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("alla");

  const antalPerKalla = useMemo(() => {
    const räknat = { alla: documents.length };
    for (const d of documents) räknat[d.source_type] = (räknat[d.source_type] || 0) + 1;
    return räknat;
  }, [documents]);

  const synliga = useMemo(() => {
    const q = query.trim().toLowerCase();
    return documents.filter((d) =>
      (source === "alla" || d.source_type === source) &&
      (!q || `${d.title || ""} ${d.filename || ""}`.toLowerCase().includes(q)));
  }, [documents, query, source]);
  // Vilken rad som är uppfälld, och vad som hämtats för den. Bara en i taget:
  // panelen är till för att granska ETT dokument, och flera öppna rader gör
  // tabellen oläslig utan att någon får veta mer.
  const [openId, setOpenId] = useState(null);
  const [text, setText] = useState(null);
  const [textError, setTextError] = useState(null);

  async function toggleText(document) {
    if (openId === document.id) {
      setOpenId(null);
      return;
    }
    setOpenId(document.id);
    setText(null);
    setTextError(null);
    try {
      setText(await documentText(user, document.id));
    } catch (caught) {
      setTextError(caught.message);
    }
  }

  async function loadMore() {
    // Nästa sida läggs till, den föregående ligger kvar. Att byta ut listan
    // hade gjort "visa fler" till "visa andra", vilket är ett annat löfte.
    const next = await documentText(user, openId, text.offset + text.limit);
    setText({ ...next, chunks: [...text.chunks, ...next.chunks] });
  }

  async function download(document) {
    setBusyId(document.id);
    setError(null);
    try {
      const { url } = await documentDownloadUrl(user, document.id);
      // Öppnas direkt och sparas aldrig. Länken är signerad, lever fem minuter
      // och fungerar för var och en som har den — den hör inte hemma i ett
      // React-tillstånd som överlever nästa rendering.
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusyId(null);
    }
  }

  async function dismiss(job) {
    setBusyId(job.id);
    setError(null);
    try {
      await dismissJob(user, job.id);
      onChange();
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusyId(null);
    }
  }

  async function remove(document) {
    // Radering kaskaderar till chunkar, vektorer och filen i lagringen och går
    // inte att ångra (AA-6.5). Bekräftelsen skriver ut vad som försvinner i
    // stället för att fråga "är du säker?", som ingen läser.
    const ok = window.confirm(
      `Radera "${document.title}"?\n\n` +
      `Fil: ${document.filename || "okänd"}\n\n` +
      `Dokumentet, dess ${document.chunks} textstycken och filen i lagringen ` +
      `tas bort permanent. Ask Aerro slutar svara ur det direkt.`);
    if (!ok) return;

    setBusyId(document.id);
    setError(null);
    try {
      const result = await deleteDocument(user, document.id);
      if (result.storage_removed === false) {
        setError(`Dokumentet är raderat, men filen i lagringen blev kvar ` +
                 `(${result.storage_path}). Hör av dig till Aerro.`);
      }
      onChange();
    } catch (caught) {
      setError(caught.message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="card">
      <h2>Uppladdade dokument</h2>

      {/* Misslyckade och pågående jobb först. Ett misslyckat jobb är det enda
          spåret av en fil som skolan trodde de laddat upp — att lägga det sist
          gör listan till en lista över det som gick bra.

          Misslyckade rader går att avfärda. En permanent röd ruta som inte går
          att bli av med gör att man slutar läsa felmeddelanden, alltså raka
          motsatsen till vad den finns för. Laddas samma fil upp igen och lyckas
          försvinner raden av sig själv — se uploads.supersede_failed_jobs. */}
      {jobs.length > 0 && (
        <ul className="jobs">
          {jobs.map((job) => (
            <li key={job.id} className={job.status === "failed" ? "failed" : "pending"}>
              <div className="jobhead">
                <span>
                  <strong>{job.title || job.filename}</strong>
                  <span className="badge">{job.status}</span>
                </span>
                {job.status === "failed" && (
                  <button className="dismiss" disabled={busyId === job.id}
                          onClick={() => dismiss(job)}>
                    {busyId === job.id ? "Avfärdar…" : "Avfärda"}
                  </button>
                )}
              </div>
              {job.reason && <p className="reason">{job.reason}</p>}
            </li>
          ))}
        </ul>
      )}

      {documents.length === 0 && jobs.length === 0 && (
        <p className="empty">
          Inga dokument än. Börja med det eleverna frågar mest om: frånvarorutin,
          läsårstider, ordningsregler, matsalsinformation.
        </p>
      )}

      {documents.length > 0 && (
        <div className="toolbar">
          <input type="search" className="search" value={query} placeholder="Sök titel eller filnamn"
                 aria-label="Sök bland dokumenten" onChange={(e) => setQuery(e.target.value)} />
          <div className="filters" role="group" aria-label="Filtrera på källa">
            {[["alla", "Alla"], ["upload", "Uppladdat"], ["email", "Utskick"],
              ["scrape", "Webbplats"]].map(([id, etikett]) => (
              antalPerKalla[id] > 0 || id === "alla" ? (
                <button key={id} className={source === id ? "chip active" : "chip"}
                        aria-pressed={source === id} onClick={() => setSource(id)}>
                  {etikett} <span className="count">{antalPerKalla[id] || 0}</span>
                </button>
              ) : null
            ))}
          </div>
        </div>
      )}

      {documents.length > 0 && synliga.length === 0 && (
        <p className="empty">Inget dokument matchar sökningen.</p>
      )}

      {synliga.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Titel</th><th>Källa</th><th>Fil</th><th>Uppladdat</th>
              <th>Format</th><th>Textstycken</th><th>Gäller till</th><th />
            </tr>
          </thead>
          <tbody>
            {/* Två rader per dokument: själva raden, och panelen när den är
                uppfälld. Fragmentet går inte att använda — en `<tr>` måste vara
                barn till `<tbody>` — så map:en returnerar en array. */}
            {synliga.flatMap((document) => [
              <tr key={document.id}>
                <td>
                  {/* Titeln är knappen. Ett dokument utan textstycken har
                      ingenting att visa — knappen skulle öppna en tom panel och
                      se trasig ut, så raden är text då. */}
                  {document.chunks > 0 ? (
                    <button className="linklike" aria-expanded={openId === document.id}
                            onClick={() => toggleText(document)}>
                      {document.title}
                    </button>
                  ) : document.title}
                  {document.thin_pages?.length > 0 && (
                    <span className="warn" title={
                      `Sidorna ${document.thin_pages.join(", ")} innehöll nästan ` +
                      `ingen text. Är de inskannade?`}>
                      {" "}⚠
                    </span>
                  )}
                </td>
                {/* AA-7: samma lista, men källan märkt per rad. Utan
                    märkningen ser en skrapad sida ut som något skolan lagt in
                    själv, och då blir det obegripligt att den inte går att
                    radera. */}
                {/* Tre värden sedan 2026-09-19. Till dess var regeln "allt som
                    inte är scrape är uppladdat", och den var korrekt så länge
                    'email' inte fanns — 7A.4b lät den stå kvar just därför.
                    Nu finns värdet, och utan den här raden märks ett utskick
                    TYST som uppladdat. */}
                <td>{KALLA[document.source_type] || "Uppladdat"}</td>
                {/* Filnamnet: två dokument kan bära samma titel.
                    matsalsinformation.pdf och matsalsinformation.docx får båda
                    titeln "matsalsinformation" ur filnamnet. */}
                <td className="filename">{document.filename || "—"}</td>
                <td>{new Date(document.created_at).toLocaleDateString("sv-SE")}</td>
                <td>{document.format || "—"}</td>
                <td>{document.chunks}</td>
                {/* Ett dokument som gått ut slutar svara utan att något
                    syns i listan. Dagarna kvar står därför i cellen: det är
                    skillnaden mellan "gick ut i går" och "finns". */}
                <td>
                  {document.valid_until || "—"}
                  {(() => {
                    const kvar = daysLeft(document.valid_until);
                    if (kvar === null) return null;
                    if (kvar < 0) return <span className="flag expired">gått ut</span>;
                    if (kvar <= 7) return <span className="flag soon">{kvar} d kvar</span>;
                    return null;
                  })()}
                </td>
                <td className="rowactions">
                  {/* Knapparna ligger i en egen låda som får radbryta. Utan den
                      stack "Ladda ner original" ut till höger om kolumnen på
                      smalare fönster — en cell med `white-space: nowrap` kan
                      inte krympa. */}
                  <div className="actions">
                  {/* Skrapade sidor går inte att radera härifrån, och knappen
                      döljs hellre än gråas ut. En scrape-körning är en
                      fullständig ersättning av källans dokument (AA-7), så en
                      radering hade återkommit vid nästa körning — ett löfte
                      knappen inte kan hålla. Att utesluta en sida hör till
                      skrapkonfigurationen, inte till den här listan. */}
                  {/* Raderingsknappen står kvar för utskick, och det är ett
                      beslut och inte ett förbiseende (7A.4b): en skrapad sida
                      kommer tillbaka vid nästa körning, ett utskick gör det
                      aldrig. Knappen kan alltså hålla sitt löfte. */}
                  {document.source_type === "scrape" ? (
                    <span className="muted">
                      Från skolans webbplats. Uppdateras vid nästa körning.
                    </span>
                  ) : (
                    <button className="danger" disabled={busyId === document.id}
                            onClick={() => remove(document)}>
                      {busyId === document.id ? "Raderar…" : "Radera"}
                    </button>
                  )}
                  {/* Bara när det FINNS ett original. Skrapade sidor och
                      utskick har ingen fil, och en knapp som svarar "den här
                      filen finns inte" är sämre än ingen knapp. Backenden
                      svarar 409 på samma fråga — spärren finns på båda ställena
                      av samma skäl som raderingsspärren.

                      `has_original` och inte `storage_path`: sökvägen kan stå
                      på jobbraden i stället för på dokumentet, och gör det för
                      allt som kommit in genom portalen. Mätt mot produktion
                      2026-09-22 var `storage_path` NULL för 100 av 100. */}
                  {document.has_original && (
                    <button className="secondary" disabled={busyId === document.id}
                            onClick={() => download(document)}>
                      Ladda ner original
                    </button>
                  )}
                  </div>
                </td>
              </tr>,
              openId === document.id && (
                <tr key={`${document.id}:text`} className="textpanel">
                  <td colSpan={8}>
                    {textError && <p className="error">{textError}</p>}
                    {!text && !textError && <p className="muted">Hämtar texten…</p>}
                    {text && (
                      <>
                        {/* Vad panelen ÄR, sagt i panelen. Den som öppnar den
                            för att leta efter en mening ur PDF:en ska genast se
                            varför den inte står där ordagrant. */}
                        <p className="muted">
                          Det här är texten som Ask Aerro läser, styckvis.
                          Avdelarna visar var texten delas — en delning mitt i
                          en tabell är det som gör svaren dåliga.
                        </p>
                        {text.chunks.map((chunk, i) => (
                          <div key={chunk.index}>
                            {i > 0 && <hr className="chunkbreak" />}
                            {chunk.heading_path?.length > 0 && (
                              <p className="headingpath">
                                {chunk.heading_path.join(" › ")}
                              </p>
                            )}
                            <p className="chunktext">{chunk.text}</p>
                          </div>
                        ))}
                        {text.chunks.length < text.chunks_total && (
                          <button className="secondary" onClick={loadMore}>
                            Visa fler ({text.chunks.length} av {text.chunks_total})
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ),
            ])}
          </tbody>
        </table>
      )}

      {error && <p className="error">{error}</p>}
    </section>
  );
}
